import type { Column, PortalRecord } from "../collection-card";
import { dash, money, str } from "../record";
import type { FieldSpec } from "./spec";

const ENTITY_TYPES = [
  { value: "P", label: "Partnership" },
  { value: "C", label: "Corporation" },
  { value: "L", label: "LLC" },
  { value: "T", label: "Trust / estate" },
  { value: "O", label: "Other" },
];
const RECEIVABLE_TYPES = [
  { value: "A", label: "Account receivable" },
  { value: "N", label: "Note receivable" },
];
const ASSET_TYPES = [
  { value: "T", label: "Tangible" },
  { value: "I", label: "Intangible" },
];

const col = (header: string, key: string): Column<PortalRecord> => ({
  header,
  cell: (r) => dash(str(r, key)),
});
const moneyCol = (header: string, key: string): Column<PortalRecord> => ({
  header,
  cell: (r) => money(r, key),
});

// --- business profile (businfo + accounting-period income/expense) ----------
export const BUSINESS_FIELDS: FieldSpec[] = [
  { name: "business_name", label: "Business name", required: true },
  { name: "dba", label: "DBA" },
  { name: "description", label: "Description" },
  { name: "entity_type", label: "Entity type", kind: "select", options: ENTITY_TYPES },
  { name: "entity_other", label: "Entity (if other)" },
  { name: "ein", label: "EIN", kind: "secret" },
  { name: "sole_prop", label: "Sole proprietor", kind: "checkbox" },
  { name: "contractor", label: "Uses contractors", kind: "checkbox" },
  { name: "street", label: "Street" },
  { name: "city", label: "City" },
  { name: "state", label: "State" },
  { name: "zip_code", label: "ZIP" },
  { name: "phone", label: "Phone" },
  { name: "website", label: "Website" },
  { name: "no_of_members", label: "Number of members", kind: "number" },
  { name: "no_of_employees", label: "Number of employees", kind: "number" },
  { name: "established", label: "Established", kind: "date" },
  { name: "deposit_freq", label: "Deposit frequency" },
  { name: "accounting_meth", label: "Accounting method" },
  { name: "period_from", label: "Accounting period from", kind: "date" },
  { name: "period_to", label: "Accounting period to", kind: "date" },
  { name: "avg_payroll", label: "Average payroll", kind: "money" },
  { name: "cash_on_hand", label: "Cash on hand", kind: "money" },
  // income
  { name: "gross_receipts", label: "Gross receipts", kind: "money" },
  { name: "gross_rental", label: "Gross rental income", kind: "money" },
  { name: "interest", label: "Interest income", kind: "money" },
  { name: "dividends", label: "Dividends", kind: "money" },
  { name: "cash_receipts", label: "Other cash receipts", kind: "money" },
  // expenses
  { name: "material_purch", label: "Materials purchased", kind: "money" },
  { name: "inventory_purch", label: "Inventory purchased", kind: "money" },
  { name: "gross_wages", label: "Gross wages", kind: "money" },
  { name: "rent", label: "Rent", kind: "money" },
  { name: "supplies", label: "Supplies", kind: "money" },
  { name: "utilities", label: "Utilities", kind: "money" },
  { name: "vehicle_cost", label: "Vehicle costs", kind: "money" },
  { name: "repairs", label: "Repairs", kind: "money" },
  { name: "insurance", label: "Insurance", kind: "money" },
  { name: "taxes", label: "Taxes", kind: "money" },
  { name: "other_debt", label: "Other expenses", kind: "money" },
];

// --- per-business sub-collections -------------------------------------------
export interface SubCollectionDef {
  segment: string;
  title: string;
  addLabel: string;
  noun: string;
  query?: string;
  columns: Column<PortalRecord>[];
  fields: FieldSpec[];
}

export const PERSONNEL: SubCollectionDef = {
  segment: "personnel",
  title: "Owners & officers",
  addLabel: "Add person",
  noun: "Person",
  columns: [
    { header: "Name", cell: (r) => dash(`${str(r, "first_name")} ${str(r, "last_name")}`.trim()) },
    col("Title", "title"),
    col("SSN", "ssn_label"),
    moneyCol("Salary", "salary"),
  ],
  fields: [
    { name: "first_name", label: "First name" },
    { name: "last_name", label: "Last name" },
    { name: "title", label: "Title" },
    { name: "ssn", label: "SSN", kind: "secret" },
    { name: "ownership", label: "Ownership %", kind: "number" },
    { name: "salary", label: "Salary", kind: "money" },
    { name: "tax_deposit", label: "Makes tax deposits", kind: "checkbox" },
    { name: "phone1", label: "Phone" },
    { name: "phone2", label: "Alt phone" },
    { name: "street", label: "Street" },
    { name: "city", label: "City" },
    { name: "state", label: "State" },
    { name: "zip_code", label: "ZIP" },
  ],
};

export const PAYMENT_PROCESSORS: SubCollectionDef = {
  segment: "payment-processors",
  title: "Payment processors",
  addLabel: "Add processor",
  noun: "Processor",
  columns: [col("Name", "name"), col("Account", "account_no_label")],
  fields: [
    { name: "name", label: "Processor name" },
    { name: "street", label: "Street" },
    { name: "csz", label: "City / state / ZIP" },
    { name: "account_no", label: "Account number", kind: "secret" },
  ],
};

export const CREDIT_CARDS: SubCollectionDef = {
  segment: "credit-cards",
  title: "Business credit cards",
  addLabel: "Add card",
  noun: "Card",
  columns: [col("Type", "card_type"), col("Bank", "issuing_bank"), col("Account", "account_no_label")],
  fields: [
    { name: "card_type", label: "Card type" },
    { name: "account_name", label: "Account name" },
    { name: "account_no", label: "Account number", kind: "secret" },
    { name: "issuing_bank", label: "Issuing bank" },
    { name: "bank_street", label: "Bank street" },
    { name: "bank_csz", label: "Bank city / state / ZIP" },
    { name: "bank_phone", label: "Bank phone" },
  ],
};

export const ACCOUNTS: SubCollectionDef = {
  segment: "accounts",
  title: "Bank & investment accounts",
  addLabel: "Add account",
  noun: "Account",
  columns: [
    col("Name", "name"),
    col("Type", "account_type"),
    col("Account", "account_no_label"),
    moneyCol("Value", "account_value"),
  ],
  fields: [
    { name: "account_type", label: "Account type" },
    { name: "name", label: "Institution" },
    { name: "account_no", label: "Account number", kind: "secret" },
    { name: "account_value", label: "Balance", kind: "money" },
    { name: "loan_balance", label: "Loan against", kind: "money" },
    { name: "credit_limit", label: "Credit limit", kind: "money" },
    { name: "min_pmt", label: "Minimum payment", kind: "money" },
    { name: "statement", label: "Statement date", kind: "date" },
    { name: "phone", label: "Phone" },
    { name: "email", label: "Email" },
    { name: "street", label: "Street" },
    { name: "csz", label: "City / state / ZIP" },
  ],
};

export const RECEIVABLES: SubCollectionDef = {
  segment: "receivables",
  title: "Accounts receivable",
  addLabel: "Add receivable",
  noun: "Receivable",
  columns: [
    col("Description", "description"),
    col("Contact", "contact"),
    moneyCol("Amount due", "amount_due"),
  ],
  fields: [
    { name: "account_type", label: "Type", kind: "select", options: RECEIVABLE_TYPES },
    { name: "description", label: "Description" },
    { name: "contact", label: "Contact" },
    { name: "phone", label: "Phone" },
    { name: "status", label: "Status" },
    { name: "due_date", label: "Due date", kind: "date" },
    { name: "invoice_no", label: "Invoice #" },
    { name: "amount_due", label: "Amount due", kind: "money" },
    { name: "street", label: "Street" },
    { name: "csz", label: "City / state / ZIP" },
  ],
};

export const ASSETS: SubCollectionDef = {
  segment: "assets",
  title: "Business assets",
  addLabel: "Add asset",
  noun: "Asset",
  columns: [
    col("Description", "description"),
    { header: "Type", cell: (r) => (str(r, "asset_type") === "I" ? "Intangible" : "Tangible") },
    moneyCol("Value", "current_value"),
    moneyCol("Loan", "loan_balance"),
  ],
  fields: [
    { name: "asset_type", label: "Asset type", kind: "select", options: ASSET_TYPES },
    { name: "description", label: "Description" },
    { name: "income_gen", label: "Generates income", kind: "checkbox" },
    { name: "purchase_date", label: "Purchase date", kind: "date" },
    { name: "current_value", label: "Current value", kind: "money" },
    { name: "loan_balance", label: "Loan balance", kind: "money" },
    { name: "monthly_pmt", label: "Monthly payment", kind: "money" },
    { name: "final_pmt_date", label: "Final payment date", kind: "date" },
    { name: "asset_street", label: "Asset street" },
    { name: "asset_csz", label: "Asset city / state / ZIP" },
    { name: "asset_county", label: "County" },
    { name: "lender", label: "Lender" },
    { name: "lender_street", label: "Lender street" },
    { name: "lender_csz", label: "Lender city / state / ZIP" },
    { name: "lender_phone", label: "Lender phone" },
  ],
};

export const AFFILIATES: SubCollectionDef = {
  segment: "affiliates",
  title: "Affiliated entities",
  addLabel: "Add affiliate",
  noun: "Affiliate",
  columns: [col("Name", "name"), col("EIN", "ein_label")],
  fields: [
    { name: "name", label: "Affiliate name" },
    { name: "ein", label: "EIN", kind: "secret" },
    { name: "street", label: "Street" },
    { name: "csz", label: "City / state / ZIP" },
  ],
};

export const MONEY_OWED: SubCollectionDef = {
  segment: "money-owed",
  title: "Money owed by the business",
  addLabel: "Add creditor",
  noun: "Creditor",
  columns: [col("Creditor", "name"), moneyCol("Balance", "loan_balance"), moneyCol("Payment", "payment_amt")],
  fields: [
    { name: "name", label: "Creditor name" },
    { name: "street", label: "Street" },
    { name: "csz", label: "City / state / ZIP" },
    { name: "loan_date", label: "Loan date", kind: "date" },
    { name: "balance_date", label: "Balance date", kind: "date" },
    { name: "loan_balance", label: "Loan balance", kind: "money" },
    { name: "payment_date", label: "Payment date" },
    { name: "payment_amt", label: "Payment amount", kind: "money" },
  ],
};

export const LIABILITIES: SubCollectionDef = {
  segment: "liabilities",
  title: "Liabilities",
  addLabel: "Add liability",
  noun: "Liability",
  columns: [col("Description", "description"), col("Creditor", "creditor"), moneyCol("Amount", "amount")],
  fields: [
    { name: "description", label: "Description" },
    { name: "amount", label: "Amount", kind: "money" },
    { name: "creditor", label: "Creditor" },
    { name: "terms", label: "Terms" },
  ],
};

export const SUB_COLLECTIONS: SubCollectionDef[] = [
  PERSONNEL,
  ACCOUNTS,
  CREDIT_CARDS,
  PAYMENT_PROCESSORS,
  RECEIVABLES,
  ASSETS,
  AFFILIATES,
  MONEY_OWED,
  LIABILITIES,
];
